import {
  computed,
  effect,
  inject,
  Injectable,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Product } from '../models/product.model';

interface PrepurchaseModalState {
  open: boolean;
  product: Product | null;
  initialQty: number;
}

/**
 * Estado global del modal de precompra, análogo a `CartDrawerService`. El
 * `PrepurchaseModalComponent` (montado una única vez en app.ts) lee
 * `state()`; los triggers (ProductCard, ProductZoomModal,
 * ProductDetailPage, PrepurchaseListPage) invocan `open(product, qty)`.
 *
 * Bloquea el scroll del body mientras el modal está abierto y cierra
 * automáticamente en cada NavigationEnd para que los links internos no
 * tengan que acordarse de cerrarlo.
 */
@Injectable({ providedIn: 'root' })
export class PrepurchaseModalService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly router = inject(Router);

  private readonly _state = signal<PrepurchaseModalState>({
    open: false,
    product: null,
    initialQty: 1,
  });

  readonly state = this._state.asReadonly();
  readonly isOpen = computed(() => this._state().open);
  readonly product = computed(() => this._state().product);
  readonly initialQty = computed(() => this._state().initialQty);

  constructor() {
    effect(() => {
      if (!this.isBrowser) return;
      document.body.style.overflow = this._state().open ? 'hidden' : '';
    });

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.close());
  }

  open(product: Product, initialQty = 1): void {
    const safeQty = Math.max(1, Math.floor(initialQty || 1));
    this._state.set({ open: true, product, initialQty: safeQty });
  }

  close(): void {
    this._state.update((s) => ({ ...s, open: false }));
  }
}
